import { ErrorHandler, Injectable, inject } from '@angular/core';
import { LogService } from './log.service';

/**
 * Global Error Handler
 * Bắt tất cả các lỗi hệ thống không được xử lý trong Angular application
 * và lưu log tập trung vào IndexedDB
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private logService = inject(LogService);

  handleError(error: Error | any): void {
    console.log('GlobalErrorHandler invoked', error);
    // Extract error information
    const errorMessage = error?.message || 'Unknown error';
    const errorStack = error?.stack || '';
    const errorName = error?.name || 'Error';

    // Determine error type
    let errorType = 'RUNTIME_ERROR';

    // Categorize common error types
    if (error?.name === 'HttpErrorResponse') {
      errorType = 'HTTP_ERROR';
    } else if (error?.rejection) {
      // Promise rejection
      errorType = 'PROMISE_REJECTION';
    } else if (errorMessage?.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
      errorType = 'CHANGE_DETECTION_ERROR';
    } else if (errorMessage?.includes('Cannot read property') || errorMessage?.includes('Cannot read properties')) {
      errorType = 'NULL_REFERENCE_ERROR';
    } else if (errorMessage?.includes('is not a function')) {
      errorType = 'TYPE_ERROR';
    } else if (errorMessage?.includes('Maximum call stack')) {
      errorType = 'STACK_OVERFLOW_ERROR';
    }

    // Parse stack trace to get location
    const stackLines = errorStack.split('\n');
    const location = this.parseStackTrace(stackLines);

    // Log error to IndexedDB
    this.logService.error(
      'SYSTEM',
      errorType,
      errorMessage,
      {
        errorName: errorName,
        errorType: errorType,
        stack: errorStack,
        location: location,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }
    );

    // Still log to console for development
    console.error('Global Error Handler caught:', error);
  }

  /**
   * Parse stack trace to extract file location and line number
   */
  private parseStackTrace(stackLines: string[]): string {
    if (stackLines.length < 2) return 'Unknown location';

    // Get first meaningful line (skip "Error" line)
    for (let i = 1; i < stackLines.length; i++) {
      const line = stackLines[i].trim();
      
      // Match patterns like "at Component.method (file.ts:123:45)"
      const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
      if (match) {
        const [, method, file, lineNum, colNum] = match;
        const fileName = file.split('/').pop() || file;
        return `${method} (${fileName}:${lineNum}:${colNum})`;
      }

      // Match patterns like "at file.ts:123:45"
      const simpleMatch = line.match(/at\s+(.+?):(\d+):(\d+)/);
      if (simpleMatch) {
        const [, file, lineNum, colNum] = simpleMatch;
        const fileName = file.split('/').pop() || file;
        return `${fileName}:${lineNum}:${colNum}`;
      }
    }

    return stackLines[1]?.trim() || 'Unknown location';
  }
}
